import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getIsPermissionModalOpen,
  getPermissionUser,
  setPermissionUser,
  togglePermissionModal,
} from "../../features/users/usersSlice";
import { GET, PUT } from "../../services/axios.service";
import {
  isHiddenPermission,
  permissionActionLabel,
  permissionModules,
  permissionName,
  permissionNote,
  permissionRequires,
} from "../../static/permission";
import { toastStyles } from "../../static/styles";
import Button from "../ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import { LoadingState } from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  tableHeadCellClass,
} from "../ui/styles";

const checkboxClass =
  "h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-[#113F67]";

const actionLabelClass =
  "flex cursor-pointer select-none items-start gap-2 rounded-md border border-gray-100 px-3 py-2 text-sm hover:border-[#34699A]";

// The permissions ticked along with a permission. Hidden ones are left out: the modal neither lists nor adds them.
const requiredBy = (name: string): string[] =>
  (permissionRequires[name] || []).filter(
    (required) => !isHiddenPermission(required)
  );

const UserPermissionModal = () => {
  const dispatch = useAppDispatch();
  const permissionUser = useAppSelector(getPermissionUser);
  const isPermissionModalOpen = useAppSelector(getIsPermissionModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  // Hidden modules and hidden single permissions aren't listed. A user keeps the hidden ones they already have: the
  // count and the bulk buttons only cover the listed names.
  const modules = permissionModules
    .map((module) => ({
      ...module,
      actions: module.actions.filter((action) => {
        const name = permissionName(module.key, action);

        return allPermissions.includes(name) && !isHiddenPermission(name);
      }),
    }))
    .filter((module) => module.actions.length > 0);

  const allNames = modules.flatMap((module) =>
    module.actions.map((action) => permissionName(module.key, action))
  );

  const selectedCount = selected.filter((name) =>
    allNames.includes(name)
  ).length;

  const handleClosePermissionModal = useCallback(() => {
    setAllPermissions([]);
    setSelected([]);
    dispatch(togglePermissionModal(false));
    dispatch(setPermissionUser(null));
  }, [dispatch]);

  const fetchPermissions = useCallback(async (id: number) => {
    setIsLoading(true);

    await GET(`/v1/admin/users/${id}/permissions`, {})
      .then((response: any) => {
        if (response.data.status === "success") {
          setAllPermissions(response.data.data.all || []);
          setSelected(response.data.data.assigned || []);
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      })
      .catch((error: any) => {
        toast.error(
          error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      });

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isPermissionModalOpen || !permissionUser?.id) return;

    fetchPermissions(permissionUser.id);
  }, [isPermissionModalOpen, permissionUser, fetchPermissions]);

  const handleToggle = (name: string) => {
    setSelected((previous) =>
      previous.includes(name)
        ? previous.filter((value) => value !== name)
        : Array.from(new Set([...previous, name, ...requiredBy(name)]))
    );
  };

  const handleToggleModule = (names: string[], isChecked: boolean) => {
    setSelected((previous) =>
      isChecked
        ? Array.from(
            new Set([
              ...previous,
              ...names,
              ...names.flatMap((name) => requiredBy(name)),
            ])
          )
        : previous.filter((value) => !names.includes(value))
    );
  };

  const handleSelectAll = () => {
    setSelected((previous) => Array.from(new Set([...previous, ...allNames])));
  };

  const handleRemoveAll = () => {
    setSelected((previous) =>
      previous.filter((name) => !allNames.includes(name))
    );
  };

  const handleSavePermissions = async () => {
    if (!permissionUser?.id) return;

    setIsSaving(true);

    await PUT(`/v1/admin/users/${permissionUser.id}/permissions`, {
      permissions: selected,
    })
      .then((response: any) => {
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          handleClosePermissionModal();
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      })
      .catch((error: any) => {
        toast.error(
          error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      });

    setIsSaving(false);
  };

  return (
    <Dialog
      open={isPermissionModalOpen === true}
      onClose={handleClosePermissionModal}
    >
      <DialogContent
        size="wide"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>પરમિશન ની વિગતો</h2>
          <p className="text-sm text-gray-500">{permissionUser?.name || "-"}</p>
        </DialogHeader>

        <div className={cx("space-y-4", dialogMobileFormClass)}>
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            {isLoading ? (
              <LoadingState />
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 border-b pb-3">
                  <span className={tableHeadCellClass}>
                    કુલ પસંદ કરેલ: {selectedCount} / {allNames.length}
                  </span>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      બધા પસંદ કરો
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveAll}
                    >
                      બધા દૂર કરો
                    </Button>
                  </div>
                </div>

                {modules.map((module) => {
                  const names = module.actions.map((action) =>
                    permissionName(module.key, action)
                  );
                  const isModuleChecked = names.every((name) =>
                    selected.includes(name)
                  );

                  return (
                    <div
                      key={module.key}
                      className="rounded-lg border border-gray-100 p-4"
                    >
                      <label className="mb-3 flex cursor-pointer select-none items-center gap-2">
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={isModuleChecked}
                          onChange={(event) =>
                            handleToggleModule(names, event.target.checked)
                          }
                        />
                        <span className={tableHeadCellClass}>
                          {module.label}
                        </span>
                      </label>

                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {module.actions.map((action) => {
                          const name = permissionName(module.key, action);

                          return (
                            <label key={name} className={actionLabelClass}>
                              <input
                                type="checkbox"
                                className={cx(checkboxClass, "mt-0.5")}
                                checked={selected.includes(name)}
                                onChange={() => handleToggle(name)}
                              />
                              <span className="min-w-0">
                                <span className="block font-medium">
                                  {permissionActionLabel[action] || action}
                                </span>
                                <span className="block text-xs text-gray-500">
                                  {permissionNote[name]}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleClosePermissionModal}
              disabled={isSaving}
            >
              રદ કરો
            </Button>

            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleSavePermissions}
              disabled={isLoading || isSaving}
            >
              {isSaving ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserPermissionModal;
